import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Leaf } from "lucide-react";

const LINES = ["Ihr grünes Paradies", "am Rande Berlins."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24 grain" data-testid="hero-section">
      <div className="pointer-events-none absolute -top-40 -right-40 h-[34rem] w-[34rem] rounded-full bg-[#E2ECE5] blur-3xl opacity-70" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-[2]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="eyebrow mb-6 flex items-center gap-2"
              data-testid="hero-eyebrow"
            >
              <Leaf size={14} /> Kleingartenanlage · Berlin-Rosenthal · seit 1918
            </motion.p>

            <h1 className="font-display font-bold tracking-tight text-[#1A382B] text-4xl sm:text-5xl lg:text-7xl leading-[1.04]" data-testid="hero-heading">
              {LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#47544A]"
            >
              Wer es schon mal bis hierher geschafft hat, ist hier genau richtig: Diese Seite hilft allen
              Pächtern – ob Mitglied oder nicht – mit Terminen, Wasser-Infos, Arbeitseinsätzen und
              wichtigen Rufnummern in unserer Anlage weiter.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <a
                href="#ueber-uns"
                data-testid="hero-cta-discover"
                className="inline-flex items-center gap-2 rounded-full bg-[#1A382B] px-7 py-3.5 text-sm font-semibold text-[#F8F7F2] transition-all duration-300 hover:bg-[#C85A32] hover:-translate-y-0.5"
              >
                Anlage entdecken
              </a>
              <a
                href="#kontakt"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 rounded-full border border-[#1A382B]/25 px-7 py-3.5 text-sm font-semibold text-[#1A382B] transition-all duration-300 hover:border-[#C85A32] hover:text-[#C85A32]"
              >
                Kontakt aufnehmen
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.05 }}
              className="mt-12 flex flex-wrap gap-x-10 gap-y-4 font-jbm text-xs tracking-wider uppercase text-[#5A826B]"
              data-testid="hero-stats"
            >
              <span><span className="text-[#1A382B] text-base font-semibold">1918</span> · Parzelliert</span>
              <span><span className="text-[#1A382B] text-base font-semibold">Rosenthal</span> · Berlin Nord</span>
              <span><span className="text-[#1A382B] text-base font-semibold">KVA</span> · Verwaltung Mitte-Nord</span>
            </motion.div>
          </motion.div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <motion.div style={{ y: imgY }} className="overflow-hidden rounded-t-[10rem] rounded-b-3xl border border-[#D8D3C5] shadow-[0_30px_60px_rgba(26,56,43,0.18)]">
                <img
                  src="https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?auto=format&fit=crop&w=1200&q=80"
                  alt="Gepflegter Kleingarten mit Beeten und Wegen"
                  className="h-[26rem] sm:h-[30rem] w-full object-cover"
                  data-testid="hero-image"
                />
              </motion.div>
              <div className="animate-float absolute -left-4 sm:-left-8 bottom-10 rounded-2xl border border-[#D8D3C5] bg-[#F8F7F2]/95 backdrop-blur px-5 py-4 shadow-lg">
                <p className="font-jbm text-[10px] tracking-[0.25em] uppercase text-[#5A826B]">Lage</p>
                <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-[#1A382B]">
                  <MapPin size={14} className="text-[#C85A32]" /> Berlin-Rosenthal
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="mt-16 flex justify-center">
          <a href="#ueber-uns" data-testid="hero-scroll-hint" aria-label="Nach unten scrollen">
            <ArrowDown className="animate-scroll-hint text-[#5A826B]" size={22} />
          </a>
        </div>
      </div>
    </section>
  );
}
